/**
 * DelegationCard — AC-AGS-M4-4
 *
 * Renders a delegation task card instead of raw JSON when a message has
 * tool_name: 'delegate_task'. Shows:
 *   - Actor name (child actor)
 *   - Task summary (first 200 chars of task_description)
 *   - Status chip (In Progress / Complete / Failed)
 *   - Link to child session
 *
 * Subscribes to Socket.io 'session:child-completed' events to update live.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { t } from '@/lib/i18n';

export interface DelegationCardProps {
  /** Raw tool_result JSON from the delegate_task tool response */
  toolResult: Record<string, unknown> | null;
  /** Tool input (contains taskDescription, etc.) */
  toolInput: Record<string, unknown> | null;
  /** Parent session ID (to subscribe to socket events) */
  parentSessionId: string;
  /** Socket.io instance (optional — card degrades gracefully without it) */
  socket?: any;
}

type DelegationStatus = 'in_progress' | 'completed' | 'failed' | 'cancelled';

const STATUS_CONFIG: Record<DelegationStatus, { label: string; icon: React.ElementType; className: string }> = {
  in_progress: { label: 'In Progress', icon: Loader2, className: 'bg-blue-50 text-blue-700' },
  completed:   { label: 'Complete',    icon: CheckCircle2, className: 'bg-green-50 text-green-700' },
  failed:      { label: 'Failed',      icon: AlertCircle,  className: 'bg-red-50 text-red-700' },
  cancelled:   { label: 'Cancelled',   icon: Clock,        className: 'bg-slate-100 text-slate-600' },
};

function StatusChip({ status }: { status: DelegationStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.in_progress;
  const Icon = cfg.icon;
  const isSpinning = status === 'in_progress';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}
      data-testid="delegation-status-chip"
    >
      <Icon className={`h-3 w-3 ${isSpinning ? 'animate-spin' : ''}`} />
      {t(cfg.label)}
    </span>
  );
}

export function DelegationCard({ toolResult, toolInput, parentSessionId, socket }: DelegationCardProps) {
  // Extract data from toolResult / toolInput
  const delegationId = (toolResult as any)?.delegationId ?? (toolResult as any)?.delegation_id ?? null;
  const childSessionId = (toolResult as any)?.childSessionId ?? (toolResult as any)?.child_session_id ?? null;
  const childActorName = (toolResult as any)?.childActorName ?? (toolResult as any)?.child_actor_name ?? null;
  const taskDescription = (toolInput as any)?.task_description ?? (toolInput as any)?.taskDescription ?? '';
  const initialStatus: DelegationStatus = (toolResult as any)?.status ?? 'in_progress';

  const [status, setStatus] = useState<DelegationStatus>(initialStatus);
  const [resolvedChildSessionId, setResolvedChildSessionId] = useState<string | null>(childSessionId);

  // Subscribe to socket events for live status updates
  useEffect(() => {
    if (!socket || !delegationId) return;

    const handleChildCompleted = (event: any) => {
      if (event.delegationId === delegationId || event.sessionId === resolvedChildSessionId) {
        setStatus(event.status === 'failed' ? 'failed' : 'completed');
        if (event.childSessionId) setResolvedChildSessionId(event.childSessionId);
      }
    };

    socket.on('session:child-completed', handleChildCompleted);
    return () => {
      socket.off('session:child-completed', handleChildCompleted);
    };
  }, [socket, delegationId, resolvedChildSessionId]);

  const taskSummary = taskDescription
    ? taskDescription.slice(0, 200) + (taskDescription.length > 200 ? '…' : '')
    : t('(no task description)');

  return (
    <div
      className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2 text-sm"
      data-testid="delegation-card"
      data-delegation-id={delegationId ?? undefined}
      data-status={status}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-blue-700 font-semibold text-xs uppercase tracking-wide">
            {t('Delegated Task')}
          </span>
          {childActorName && (
            <span className="text-slate-600 text-xs truncate">→ {childActorName}</span>
          )}
        </div>
        <StatusChip status={status} />
      </div>

      {/* Task summary */}
      <p className="text-slate-700 text-xs leading-relaxed" data-testid="delegation-task-summary">
        {taskSummary}
      </p>

      {/* Child session link */}
      {resolvedChildSessionId && (
        <div className="pt-1">
          <Link
            to={`/roros/agent-sessions/${resolvedChildSessionId}`}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
            data-testid="delegation-child-session-link"
          >
            {t('View child session')}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {!resolvedChildSessionId && delegationId && (
        <span className="text-xs text-slate-400 italic" data-testid="delegation-no-session">
          {t('Child session starting…')}
        </span>
      )}
    </div>
  );
}
